import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NormalizedCacheObject } from '@apollo/client/core';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';



import { cloneDeep } from 'lodash';
import { BehaviorSubject, lastValueFrom, Observable, Subject } from 'rxjs';

import { SubSink } from 'subsink';

import { environment } from '../../../environments/environment';

import { AuthenticateGQL, AuthenticateMutationVariables, AuthenticateOutput, ConfirmSecretGQL, ConfirmSecretMutationVariables, DeauthenticateGQL, Disable2FaGQL, Disable2FaMutationVariables, GenerateSecretGQL } from '../../../generated/graphql.generated';
import { JWTPayload, UserProfile } from '../../interfaces/auth';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { currentTimeSeconds, MS_TEN_SECONDS, resolveMilliseconds, S_ONE_HOUR } from '../../time';

import { AuthActions } from './auth.actions';
import { GraphQLModule } from './graphql.module';
import { jwtDecode } from './jwtDecode';

export type AuthState = typeof authStates[number];
export const authStates = [
  // user has been successfully authenticated
  'authenticated',
  // user has been successfully reauthenticated
  'reauthenticated',
  // user deauthenticated, cache cleared
  'deauthenticated',
  // warning that cache will be cleared very soon & queries refired
  'deauthenticating',
] as const;

export type ConnectionStatus = typeof connectionStatuses[number];
export const connectionStatuses = [
  // initial state
  'preparing',
  // called when query succeeds and not in connected state
  'connected',
  // called when we are confirmed disconnected by polling
  'disconnected',
  // called when polling is currently active
  'disconnected-polling',
] as const;


@Injectable({
  providedIn: 'root'
})
export class PlusAuthenticationService implements OnDestroy {

  subs = new SubSink();

  public homeURL = '/';
  public loginURL = '/login';
  public autoAccessTokenRenewalOffset = 0;

  /**
   * Whether this service should handle navigation after login
   * If the localstorage value with key pre_login_url is set
   * it will use that and then clear that localstorage key
   * Otherwise it will go to homeURL
   */
  public shouldNavigateAfterLogin = true;

  // Local storage keys
  public lsKeys = {
    preLoginURL: 'pre_login_url',
    userInfo: 'user_info',
    accessTokenExpiration: 'access_token_expiration',
    refreshTokenExpiration: 'refresh_token_expiration',
  };

  // session storage keys (valid for that tab)
  public ssKeys = {
    desiredZoneId: 'desired_zone_id',
    zone: 'mr_zone',
    role: 'mr_role',
  };

  public polling = false;
  public pollingInterval = MS_TEN_SECONDS;

  public connectionStatus = new BehaviorSubject<ConnectionStatus>('preparing');
  public authState = new BehaviorSubject<AuthState>(this.user ? 'authenticated' : 'deauthenticated');

  /**
   * Bootstrap auth promise. Set by bootstrapAuth() and awaited by the
   * UserAuthGuard so the guard doesn't redirect to login before the
   * initial cookie-based auth check has completed.
   *
   * Undefined when no bootstrap has run yet — and after deauthentication,
   * which invalidates the cached result so a later guard/redirect bootstrap
   * re-checks the cookie session instead of reusing the stale `true`.
   */
  public bootstrapAuthPromise?: Promise<boolean>;

  private userProfile?: UserProfile;

  /**
   * In-flight deauthentication promise. Concurrent deauthentication triggers
   * (e.g. several in-flight GraphQL responses all carrying x-deauthenticated)
   * must not each run the full cleanup: a later call finishes after the first
   * one has navigated to the login page, and would clear pre_login_url and
   * then save the login URL as the "return" destination — which the auth
   * redirect treats as stale, falling back to /dashboard instead of the
   * protected page the user was on.
   */
  private deauthInFlight?: Promise<AuthState>;

  /**
   * Incremented by every deauthentication. authenticate() captures this when
   * a request starts and discards the response if a deauthentication happened
   * in between: the backend only clears cookies in the deauth response and
   * does not revoke the refresh token used by a request that had already
   * started, so a stale completion could otherwise restore authentication
   * state (updateAuthStatus) after deauthentication cleanup.
   */
  private bootstrapGeneration = 0;

  /**
   * The user whose session this document was booted with (snapshotted at
   * service construction, so the id of the session this tab's *in-memory*
   * state belongs to — before any other tab could churn the shared cookie
   * jar / localStorage).
   *
   * Deauthentication no longer reloads the document, and cookie-based
   * restores can happen without one, so AuthRedirectComponent uses this to
   * tell whether an in-place restore is safe: restoring a *different* user
   * (e.g. someone else signed in on a second tab, then the first tab
   * bounces through login and restores from the cookie) would keep the
   * previous user's NgRx store and any singleton caches that don't reset on
   * the deauth broadcast. The restore then switches to a full page load.
   */
  public readonly bootUserId: string | undefined = this.jwtPayload?.id;

  /**
   * Set by updateAccessToken when an authentication is in progress.
   * If set, the application is currently trying to authenticate
   */
  private accessTokenUpdatePromise: Promise<AuthState>;

  /**
   * Key value store of context cache serialized state
   * Key is in the format zone:role (see getCurrentContextId)
   * this is set by the restoreCurrentContextCache method
   * and read by the restoreContextCache method.
   */
  private cacheStorage: { [key: string]: NormalizedCacheObject } = {};

  constructor(
    private apollo: Apollo,
    private authGql: AuthenticateGQL,
    private deauthGql: DeauthenticateGQL,
    private confirmSecretGql: ConfirmSecretGQL,
    private disable2FAGql: Disable2FaGQL,
    private generateSecretGql: GenerateSecretGQL,
    private graphqlModule: GraphQLModule,
    private router: Router,
    private http: HttpClient,

    // gotta be careful with circular dependencies here
    // private perms: PermissionsService,
    private localNotify: FreyaNotificationsService,
    private store: Store,
  ) {
    this.subs.sink = this.authState.subscribe((_authState) => {
      this.store.dispatch(AuthActions.authUpdated({
        user: this.user,
      }));
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * The currently contexted zone id, alias of GraphqlModule.zone.value
   */
  public get contextedZoneId() {
    return this.graphqlModule.zone.value;
  }

  /**
   * The currently contexted role id, alias of GraphqlModule.role.value
   */
  public get contextedRoleId() {
    return this.graphqlModule.role.value;
  }

  /**
   * Returns the users profile, based from the current JWT.
   * Returns undefined if Refresh Token is invalid or expired.
   * Will still return if access token is invalid or expired
   */
  public get user(): UserProfile {
    const payload = this.jwtPayload;
    if (!payload) { return undefined; }

    const expiration = this.refreshTokenExpiration;
    if (!expiration || expiration < Date.now() / 1000) {
      return undefined;
    }

    if (this.userProfile && this.userProfile.id === payload.id) {
      return this.userProfile;
    }

    return {
      ...payload,
      createdAt: payload.createdAt ? new Date(payload.createdAt * 1000) : undefined,
      updatedAt: payload.updatedAt ? new Date(payload.updatedAt * 1000) : undefined,
      deletedOn: payload.deletedOn ? new Date(payload.deletedOn * 1000) : undefined,
    };
  }

  /**
   * Sets the expiration time for the json web token in unix seconds
   *
   * @param time is the expiration time before formatted to string
   */
  public set jwtPayload(payload: JWTPayload) {
    if (payload) {
      localStorage.setItem(this.lsKeys.userInfo, JSON.stringify(payload));
    } else {
      localStorage.removeItem(this.lsKeys.userInfo);
    }

  }

  /**
   * Retrieves the expiration time for the json web token in unix seconds
   */
  public get jwtPayload(): JWTPayload {
    const strPayload = localStorage.getItem(this.lsKeys.userInfo);

    return strPayload ? JSON.parse(strPayload) : undefined;
  }

  /**
   * Sets the expiration time for the json web token in unix seconds
   *
   * @param time is the expiration time before formatted to string
   */
  public set accessTokenExpiration(time: number) {
    if (time) {
      localStorage.setItem(this.lsKeys.accessTokenExpiration, time.toString(10));
    } else {
      localStorage.removeItem(this.lsKeys.accessTokenExpiration);
    }
  }

  /**
   * Retrieves the expiration time for the json web token in unix seconds
   */
  public get accessTokenExpiration(): number {
    return +localStorage.getItem(this.lsKeys.accessTokenExpiration);
  }
  /**
   * Sets the expiration time for the json web token in unix seconds
   *
   * @param time is the expiration time before formatted to string
   */
  public set refreshTokenExpiration(time: number) {
    if (time) {
      localStorage.setItem(this.lsKeys.refreshTokenExpiration, time.toString(10));
    } else {
      localStorage.removeItem(this.lsKeys.refreshTokenExpiration);
    }
  }

  /**
   * Retrieves the expiration time for the refresh token in unix seconds
   */
  public get refreshTokenExpiration(): number {
    return +localStorage.getItem(this.lsKeys.refreshTokenExpiration);
  }

  /**
   * @returns true if authenticated
   */
  public isAuthenticated(): boolean {
    return Boolean(this.user);
  }

  /**
   * Toast for session expired, sign in again
   */
  public notifySessionExpired() {
    this.localNotify.addToast.next({
      summary: 'Session Expired',
      detail: 'Please sign in again.',
      closable: true,
      life: S_ONE_HOUR,
      severity: 'warn',
    });
  }

  /**
   * Checks the refresh token and access token for validity
   *
   * If refresh token has expired, will log the user out
   * If access token has expired, will try to update the access token.
   *
   * @returns current auth state after updates
   */
  public async checkAuthStatus(
    updateAccessToken = false,
    logoutIfRefreshTokenInvalid = true,
  ) {

    const currentTimeMS = Date.now() / 1000;

    if (
      this.refreshTokenExpiration
      && this.refreshTokenExpiration < currentTimeMS
      && logoutIfRefreshTokenInvalid
    ) {
      this.notifySessionExpired();
      await this.logout();
    } else if (this.accessTokenExpiration) {
      // If our access token has expired then renew it
      const expiresIn = this.accessTokenExpiration - currentTimeSeconds();
      const accessTokenExpired = expiresIn <= 0;
      const signedIn = this.user;
      if (accessTokenExpired && signedIn && updateAccessToken) {
        await this.updateAccessToken();
      }
    }

    return this.authState.value;
  }

  /**
   * Bootstrap authentication from HTTP-only cookies.
   *
   * Called by APP_INITIALIZER before the app boots. If localStorage already
   * has a user (page reload), this is a no-op. If localStorage is empty but
   * the browser has an httpOnly refresh-token cookie (e.g. after FA callback
   * redirect), this calls authenticate({}) which sends the GraphQL mutation
   * with no params — the backend uses the cookie to authenticate and returns
   * a wrapper JWT, which populates localStorage.
   *
   * The UserAuthGuard awaits `bootstrapAuthPromise` before deciding whether
   * to redirect to login, preventing a redirect loop when cookies exist but
   * localStorage hasn't been hydrated yet.
   */
  public bootstrapAuth(): Promise<boolean> {
    if (this.bootstrapAuthPromise) {
      return this.bootstrapAuthPromise;
    }

    // Already authenticated (localStorage has user) — no bootstrap needed.
    if (this.user) {
      this.bootstrapAuthPromise = Promise.resolve(true);
      return this.bootstrapAuthPromise;
    }

    // Skip bootstrap on auth pages that don't need authentication
    // (logged-out, reset-password, signup) — calling authenticate({}) there
    // is pointless. The login page is deliberately NOT skipped: it is the
    // cookie-restore point after an x-deauthenticated bounce, and the GraphQL
    // layer already skips deauth handling on auth pages, so a failed
    // authenticate({}) here cannot trigger a goToLogin loop.
    if (typeof window !== 'undefined') {
      const pathname = window.location.pathname;
      const onAuthPage = pathname.startsWith('/auth/') || pathname === '/login';
      const onLoginPage = pathname.startsWith('/auth/login') || pathname === '/login';
      if (onAuthPage && !onLoginPage) {
        this.bootstrapAuthPromise = Promise.resolve(false);
        return this.bootstrapAuthPromise;
      }
    }

    // Requirement: #6780/#6779 - if a deep link (e.g. a job link from a screen
    // pop or escalate email) carries ?zone=<id>, pass it through so the backend
    // contexts into the zone the link points at, instead of falling back to the
    // user's first contextable zone — which, for a multi-zone user, may not be
    // the zone this link's entity lives in.
    const zone = typeof window !== 'undefined'
      ? new URL(window.location.href).searchParams.get('zone') || undefined
      : undefined;

    this.bootstrapAuthPromise = new Promise<boolean>((resolve) => {
      const generation = this.bootstrapGeneration;
      this.authenticate({ zone }).subscribe({
        next: (authRes) => {
          if (generation !== this.bootstrapGeneration) {
            // Deauthentication happened while the request was in flight;
            // discard the result (authenticate() has already skipped
            // updateAuthStatus for it).
            resolve(false);
          } else if (authRes && !authRes.invalidCode && authRes.jwt) {
            resolve(true);
          } else {
            resolve(false);
          }
        },
        error: () => resolve(false),
      });
    });

    return this.bootstrapAuthPromise;
  }

  /**
   * Set access token expirations
   * If our refresh token is expired, log out.
   *
   * If there was a specific error where the user must
   * require more information then skip setting state
   */
  public async updateAuthStatus(res: AuthenticateOutput = {}): Promise<void> {
    const prestate: Partial<JWTPayload> = this.jwtPayload;

    // reset if no user id was returned
    if (!res.userId) {
      this.accessTokenExpiration = undefined;
      this.refreshTokenExpiration = undefined;
      this.jwtPayload = undefined;
    }

    if (res.jwt && res.userId) {
      this.jwtPayload = jwtDecode(res.jwt);
    } else if (res.validJWT === false) {
      this.accessTokenExpiration = undefined;
    }

    if (res.jwtExpires) {
      this.accessTokenExpiration = res.jwtExpires;
    }

    if (res.refreshTokenExpires) {
      this.refreshTokenExpiration = res.refreshTokenExpires;
    }

    if (res.validRefreshToken === false) {
      this.refreshTokenExpiration = undefined;
    }

    if (prestate && !this.jwtPayload) {
      await this.logout(true);
    } else if (!prestate && this.jwtPayload) {
      this.graphqlModule.reinitWebsocket();
      this.authState.next('authenticated');
    } else if (prestate && this.jwtPayload) {
      this.graphqlModule.reinitWebsocket();
      this.authState.next('reauthenticated');
    }
  }

  /**
   * deauthenticates by calling backend
   * then calls onDeauthenticated to clear the state and
   * optionally navigate to login
   */
  public async logout(doNavigate = true) {
    await lastValueFrom(this.deauthGql.mutate({}, {
      context: {
        headers: {
          'x-no-auth': 'true',
        },
      },
    }))
    .catch((err) => {
      const errors = err.networkError?.error?.errors;
      if (errors) {
        const [error] = errors;
        if (error.message.includes('Cannot resolve zone from domain')) {
          this.localNotify.warning(
            `Cannot deauthenticate - has the system been setup?`,
            error.message,
          );
        }
      } else {
        console.error('[Auth] Logout failed:', err);
      }
    });

    this.store.dispatch(AuthActions.deauthenticated());

    // When using FusionAuth, redirect to FA's logout endpoint to clear
    // the SSO cookie. FA shows a "logging out" page for 2 seconds then
    // redirects to post_logout_redirect_uri. We use /api/auth/logout
    // (in authorizedRedirectURLs) which clears our cookies and redirects
    // to the frontend's /auth/logged-out page.
    if (doNavigate && environment.authUrl && environment.authClientId) {
      const logoutRedirectUri = environment.authCallbackUrl.replace('/api/auth/callback', '/api/auth/logout');
      const faLogoutUrl = `${environment.authUrl}/oauth2/logout`
        + `?client_id=${environment.authClientId}`
        + `&post_logout_redirect_uri=${encodeURIComponent(logoutRedirectUri)}`;
      window.location.href = faLogoutUrl;
      return this.authState.value;
    }

    return this.onDeauthenticated(doNavigate);
  }

  /**
   * Fired directly after we have deauthenticated
   *
   * Clears storage, resets cache, resets permissions, resets current zones,
   * clears the store, and optionally navigates to login.
   *
   * Fires the deauthenticating and deauthenticated events.
   *
   * @param doNavigate whether we should navigate after we've deauthenticated.
   * @returns current auth state
   */
  public async onDeauthenticated(doNavigate = true): Promise<AuthState> {
    if (this.deauthInFlight) {
      // A concurrent deauthentication is already running: it has captured the
      // return URL and will perform cleanup + navigation. Join it instead of
      // re-running cleanup after it (which would wipe the saved return URL).
      return this.deauthInFlight;
    }

    // Capture the destination before any cleanup: resetCache()/clearStore()
    // await, so by the time navigation runs a concurrent navigation may have
    // made router.url report the login page instead of the protected URL.
    const returnTo = doNavigate && this.router.url && this.router.url !== '/'
      ? this.router.url
      : undefined;

    const run = this.completeDeauthentication(doNavigate, returnTo);
    this.deauthInFlight = run.finally(() => {
      this.deauthInFlight = undefined;
    });
    return this.deauthInFlight;
  }

  /**
   * Cleanup + navigation body of onDeauthenticated. Only ever runs for the
   * first concurrent caller (see deauthInFlight coordination above).
   */
  private async completeDeauthentication(doNavigate: boolean, returnTo?: string): Promise<AuthState> {
    // warn impending de-auth so queries can be unsubbed from
    this.authState.next('deauthenticating');

    // Invalidate the cached bootstrap result. It holds `true` from the last
    // successful bootstrap; with the no-reload deauth flow, AuthRedirectComponent
    // and UserAuthGuard would otherwise reuse it and navigate into protected
    // routes without a restored session. Undefined makes bootstrapAuth() run a
    // fresh cookie-based check next time.
    this.bootstrapAuthPromise = undefined;
    // Any authenticate() request still in flight started under the previous
    // session; bumping the generation makes it discard its response instead
    // of restoring auth state after this cleanup.
    this.bootstrapGeneration++;

    // HTTP Only cookies have now been cleared.
    localStorage.clear();

    // clear session storage on deauth for now
    // In the future we may leave the zone here
    // so the user can log back into the same zone + receive extra info
    sessionStorage.clear();
    await this.resetCache();
    // this.perms.reset();
    this.graphqlModule.zone.next(undefined);
    this.graphqlModule.role.next(undefined);
    // this.apollo.client.

    this.graphqlModule.reinitWebsocket();
    await this.apollo.client.clearStore()
      .catch();

    // If we want to route to login page automatically. goToLogin saves the
    // destination captured before cleanup (returnTo) as pre_login_url, so the
    // login redirect — and the OAuth2 `state` param the login page sends to
    // FusionAuth — can bring the user back to the page they were on after
    // they sign in again.
    if (doNavigate) {
      this.goToLogin({}, returnTo);
    }

    // Notify apllication that the user has been logged out
    this.authState.next('deauthenticated');

    return this.authState.value;
  }

  /**
   * Whether we are in the process of deauthenticating or are currently deauthenticated.
   *
   * @returns true if deauthenticating/deauthenticated
   */
  isDeauthed() {
    return this.authState.value.startsWith('deauth');
  }

  /**
   * @returns Returns true if the application is in the process of authenticating
   * or deauthenticating. Returns false if authenticated or deauthenticated but not query is
   * in flight
   */
  isDeauthenticating() {
    return this.authState.value === 'deauthenticating';
  }

  /**
   * @returns the current context in zone:role format
   */
  public getCurrentContextId() {
    return `${this.graphqlModule.zone.value}:${this.graphqlModule.role.value}`;

  }

  /**
   * Store the current cache in a variable associated with the current context
   */
  public storeCurrentContextCache() {
    const ctxId = this.getCurrentContextId();
    // https://www.apollographql.com/docs/react/api/cache/InMemoryCache/#extract
    this.cacheStorage[ctxId] = cloneDeep(this.apollo.client.cache.extract(false));
  }

  /**
   * Restore a specific context's cache
   *
   * @param ctxId Context ID, in zone:role format.
   */
  public async restoreContextCache(ctxId: string) {
    const cache = this.apollo.client.cache;
    if (this.cacheStorage[ctxId]) {
      // console.log('cache restoring', cache, this.cacheStorage[ctxId]);
      // console.log(this.cacheStorage[ctxId]);
      // cache must be reset beforehand because restore doesn't
      // completely clear the cache.
      await cache.reset();
      cache.restore(this.cacheStorage[ctxId]);
    } else {
      await cache.reset();
    }
  }

  /**
   * Completely clear the cache and cache storage.
   */
  public async resetCache() {
    this.cacheStorage = {};
    await this.apollo.client.cache.reset();
  }

  /**
   * Sets the current context in terms of zone:role combos.
   *
   * Stores the current cache before it is set and restores the new zone cache.
   * Emits provided graphqlModule.zone and graphqlModule.role states.
   * Resets permissions and refeteches observable queries for a specified context.
   *
   * @param zone Zone to context into
   * @param role Optional, role will be resolved by backend if not provided.
   */
  async setContext(
    zone: string,
    role?: string,
  ) {
    // Increment the ref-count so the auth-link response handler suppresses
    // zone/role overwrites from stale in-flight responses for the entire
    // duration of this switch. Using a counter (not a boolean) means
    // overlapping setContext() calls don't clear the guard prematurely.
    this.graphqlModule.zoneLoadingCount++;
    try {
      this.storeCurrentContextCache();
      await this.restoreContextCache(`${zone}:${role}`);
      this.graphqlModule.zone.next(zone);
      this.graphqlModule.role.next(role);
      // Persist to sessionStorage (per-tab) + localStorage (fallback for new tabs).
      try {
        sessionStorage.setItem(this.ssKeys.zone, zone);
        sessionStorage.setItem(this.ssKeys.role, role || '');
        localStorage.setItem(this.ssKeys.zone, zone);
        localStorage.setItem(this.ssKeys.role, role || '');
      } catch { /* storage may be unavailable */ }
      this.graphqlModule.reconnectWebsocket();
      // this.perms.reset();

      // Incase of an issue with the observables return after 5 seconds
      await Promise.race([this.apollo.client.reFetchObservableQueries().catch(), resolveMilliseconds(5000)]);
    } finally {
      this.graphqlModule.zoneLoadingCount--;
    }

    // Update the URL after we have confirmed the zone change
    this.updateZoneInNavigation();
  }

  /**
   * Calls PlusAuthenticationService.authenticate to retrieve a new JWT into the cookie.
   * Guarantees this is only called once at a time, if a query is in flight you can
   * await the returned promise and only one authenticate will be called.
   *
   * Used in graphql.module
   *
   * @returns a promise that resolves when the access token has been updated
   */
  updateAccessToken() {
    if (this.accessTokenUpdatePromise) {
      return this.accessTokenUpdatePromise;
    }

    this.accessTokenUpdatePromise = new Promise((resolve, reject) => {
      const sub = this.authenticate({}).subscribe(() => {
        sub.unsubscribe();
        delete this.accessTokenUpdatePromise;
        resolve(this.authState.value);
      }, (err) => {
        sub.unsubscribe();
        delete this.accessTokenUpdatePromise;
        reject(err);
      });
    });

    return this.accessTokenUpdatePromise;
  }

  /**
   * Authenticate into the system with a username, password,
   * refresh token, or any other auth method.
   *
   * System will then set the appropriate cookies, localStorage, and sessionStorage.
   *
   * @param variables credentials to authenticate with
   * @param updateAuthStatus Whether you want to set the appropriate state afterwards
   * and emit events. Set to false to just check auth credentials (eg when setting 2fa)
   * @returns observable that completes when fully authenticated or when there is an error
   */
  public authenticate(
    variables: AuthenticateMutationVariables = {},
  ): Observable<AuthenticateOutput> {

    const s = new Subject<AuthenticateOutput>();

    // If we don't have a refresh token, let the system set our zone
    // instead of enforcing it
    if (variables.refreshToken) {
      variables.zone = variables.zone || undefined;
    }

    // rememberLength: true=30 days, false=24 hours
    // Capture the generation so a deauthentication while this request is in
    // flight invalidates its response (see bootstrapGeneration).
    const generation = this.bootstrapGeneration;

    this.authGql.mutate(variables, {
      context: {
        headers: {},
      },
    }).subscribe(async (res) => {
      if (!res?.data) { return; }
      const authRes = res.data.authenticate;
      if (generation !== this.bootstrapGeneration) {
        // Deauthenticated while this request was in flight: the backend
        // cleared cookies but did not revoke the refresh token this request
        // was sent with, so a success response must not restore
        // authentication state. Skip updateAuthStatus and report the attempt
        // as not-authenticated instead.
        s.next({
          invalidCode: 'DEAUTHENTICATED_DURING_AUTH',
          invalidReason: 'Session ended during authentication',
        });
        s.complete();
        return;
      }
      if (!authRes.invalidCode) {
        /**
         * Only update the auth status if we successfully authenticated
         * The deauthentication headers will handle what to do
         * if we have encountered an error that requires us to deauth,
         * such as the access token expiring.
         */
        await this.updateAuthStatus(authRes);
      }

      s.next(res.data?.authenticate);
      s.complete();
    }, (e) => {
      console.error('[Auth] Authentication failed:', e);
      s.next({
        invalidCode: 'ERR',
        invalidReason: e.message ? e.message.replace('GraphQL error: ', '') : 'Auth error',
      });
      s.complete();
    });

    return s.asObservable();
  }

  /**
   * Redirects to the login page.
   */
  public goToLogin(
    queryParams: any = {},
    url?: string,
  ): void {

    if (url) {
      localStorage.setItem(this.lsKeys.preLoginURL, url);
    }

    // navigate to login
    this.router.navigate([this.loginURL], { queryParams });

    // When the user logs in they will be redirected to this.router.url
  }

  /**
   * Returns and clears the recorded pre-login destination: `pre_login_url`
   * when set, otherwise the home URL. Consumed-on-use so a later login
   * cannot resurrect a stale destination.
   *
   * Internal paths only: the value is consumed into router navigations and,
   * for a cross-user cookie restore, into `window.location.assign` (via
   * `navigateFullPage`). The key is written by the app itself, but an entry
   * that would navigate off-origin must not leave this method: the stored
   * value is resolved against the current origin and only returned when the
   * resolution stays same-origin — which rejects protocol-relative forms in
   * both of their spellings (`//attacker.example` AND `/\attacker.example`,
   * since WHATWG parsing treats `\` as `/` on HTTP(S)) as well as absolute
   * foreign URLs. Query strings and anchors on legitimate paths are kept as
   * recorded.
   */
  public consumePostLoginUrl(): string {
    const stored = localStorage.getItem(this.lsKeys.preLoginURL) || '';
    localStorage.removeItem(this.lsKeys.preLoginURL);
    let url = this.homeURL || '/';
    if (stored) {
      try {
        if (new URL(stored, window.location.origin).origin === window.location.origin) {
          url = stored;
        }
      } catch { /* unparseable — keep the home URL */ }
    }
    return url;
  }

  /**
   * Full-document navigation. Deliberately bypasses the Angular router: the
   * point of a `location.assign` after a successful (cross-user) cookie
   * restore is to boot a clean application without the previous user's NgRx
   * feature state and singleton caches in memory.
   *
   * A separate method (not a raw `window.location.assign` at call sites)
   * keeps the navigation observable: specs substitute a spy here instead of
   * navigating karma's own iframe.
   */
  public navigateFullPage(url: string): void {
    window.location.assign(url);
  }

  /**
   * Redirects to the post-login landing URL.
   *
   * Resolves with the landing URL when the SPA navigation succeeded, or
   * false when nothing navigated:
   *  - post-login navigation is disabled (shouldNavigateAfterLogin), or
   *  - the SPA navigation failed — a guard cancelled/errored or a lazy
   *    chunk failed to load (long-lived tab across a deploy). A failed
   *    navigation previously left the user staring at "Redirecting…"; here
   *    it recovers with a full document load at the same URL, which also
   *    reloads the stale chunk.
   */
  public navigateAfterLogin(targetUrl?: string): Promise<false | string> {
    if (!this.shouldNavigateAfterLogin) { return Promise.resolve(false); }

    const url = targetUrl !== undefined ? targetUrl : this.consumePostLoginUrl();

    return this.router.navigateByUrl(url).then(
      (navigated) => {
        if (navigated) { return url; }
        // A guard cancelled the navigation (a guard returning a UrlTree is
        // fine — Angular carries the promise through the redirect): recover
        // with a full page load. The recorded destination was consumed
        // above, so a later login stays on the home page.
        this.navigateFullPage(url);
        return false;
      },
      (error) => {
        console.error('[Auth] Post-login navigation failed:', error);
        this.navigateFullPage(url);
        return false;
      },
    );
  }

  async checkConnection() {

    const data = await this.http.get(`/api/ready`, {
      headers: {},
      responseType: 'text',
    }).toPromise().catch(() => '');

    const ok = data === 'OK';
    return ok;
  }

  public async pollConnection() {
    if (this.polling) { return; }
    this.polling = true;
    let lastCheck = 0;

    const interval = setInterval(() => {
      this.connectionStatus.next('disconnected-polling');
    }, this.pollingInterval);

    const stop = () => {
      sub.unsubscribe();
      clearInterval(interval);
      this.polling = false;
      //when user loses internet connection ang goes back online, reFetchObservableQueries
      //causes 20+ queries being refetched on estimator page, that causes long loading
      //when user works on field and has unstable connection
      //this.apollo.client.reFetchObservableQueries();
    };

    const sub = this.connectionStatus.subscribe(async (status) => {
      if (status === 'connected') {
        return stop();
      }

      // skip if we just checked for extra
      // loop protection and to prevent duplicates
      if (Date.now() - lastCheck < 500) {
        return;
      }

      const ok = await this.checkConnection();
      lastCheck = Date.now();
      if (ok) {
        this.connectionStatus.next('connected');
        return stop();
      } else if (this.connectionStatus.value !== 'disconnected') {
        this.connectionStatus.next('disconnected');
      }
    });
  }

  /**
   * Generate a secret and assign that secret to a user, secret isn't activated unti confirmed.
   *
   * @returns Secret used for generating 2FA codes
   */
  public generateSecret() {
    return this.generateSecretGql.mutate({});
  }

  /**
   * Use a token to confirm that the generated secret has been saved by the user.
   *
   * @param input Token Value generated by the secret
   * @returns
   */
  public confirmSecret(input: ConfirmSecretMutationVariables) {
    return this.confirmSecretGql.mutate(input);
  }

  /**
   * Remove the 2FA level of authentication for this user.
   *
   * @param input Valid 2FA Token Generated
   * @returns
   */
  public disable2FA(input: Disable2FaMutationVariables) {
    return this.disable2FAGql.mutate(input);
  }

  /**
   * Sets the current zone in the location search/query params
   * using native angular navigation.
   *
   * Keeps existing search parameters, just adds zone.
   */
  updateZoneInNavigation() {

    // const queryParamMap = this.router.routerState.snapshot.root.queryParamMap;
    // const queryParamZone = queryParamMap.get('zone');

    const url = new URL(location.toString());
    const queryParamZone = url.searchParams.get('zone');
    const zone = this.graphqlModule.zone.value;

    // If the queryParamsZone is different than the current zone
    // and the user is authenticated,
    // then set the current zone in the url params
    if (queryParamZone !== zone && this.user) {
      const queryParams = {};
      url.searchParams.set('zone', zone);
      url.searchParams.forEach((v, k) => queryParams[k] = v);
      // console.log(url.pathname, zone, queryParams);
      this.router.navigate([url.pathname], {
        replaceUrl: true,
        queryParams,
        queryParamsHandling: 'merge',
        preserveFragment: true,
        // skipLocationChange: true,
      });
    }

  }


}
